import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const InlineNoticeContext = createContext();

export const useInlineNotice = () => {
  const context = useContext(InlineNoticeContext);
  if (!context) {
    throw new Error('useInlineNotice must be used within an InlineNoticeProvider');
  }
  return context;
};

export const InlineNoticeProvider = ({ children }) => {
  const [notices, setNotices] = useState({});
  const timeoutsRef = useRef({});

  const clearInlineNotice = useCallback((scope = 'global') => {
    if (timeoutsRef.current[scope]) {
      clearTimeout(timeoutsRef.current[scope]);
      delete timeoutsRef.current[scope];
    }
    setNotices(prev => {
      const next = { ...prev };
      delete next[scope];
      return next;
    });
  }, []);

  const showInlineNotice = useCallback((message, options = {}) => {
    const {
      scope = 'global',
      type = 'success',
      duration = 3000
    } = options;

    setNotices(prev => ({
      ...prev,
      [scope]: {
        message,
        type
      }
    }));

    if (timeoutsRef.current[scope]) {
      clearTimeout(timeoutsRef.current[scope]);
    }

    if (duration > 0) {
      timeoutsRef.current[scope] = setTimeout(() => {
        clearInlineNotice(scope);
      }, duration);
    }
  }, [clearInlineNotice]);

  const value = useMemo(() => ({
    notices,
    showInlineNotice,
    clearInlineNotice
  }), [notices, showInlineNotice, clearInlineNotice]);

  return (
    <InlineNoticeContext.Provider value={value}>
      {children}
    </InlineNoticeContext.Provider>
  );
};
